import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/authService';
import ConfirmModal from '../components/ConfirmModal';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    checkAuth();

    // Escuchar evento de sesión expirada por token 401 (api.js)
    const handleSessionExpired = () => {
      setSessionExpired(true);
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('token');
    const usuarioGuardado = localStorage.getItem('usuario');

    if (token && usuarioGuardado) {
      try {
        const usuarioData = JSON.parse(usuarioGuardado);
        setUsuario(usuarioData);
      } catch (error) {
        console.error('Error al parsear usuario:', error);
        logout();
      }
    }
    setLoading(false);
  };

  const login = async (nombreUsu, clave) => {
    try {
      const response = await authService.login(nombreUsu, clave);
      
      if (response.success) {
        const { token, usuario: usuarioData } = response.data;
        
        // Guardar en localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('usuario', JSON.stringify(usuarioData));
        
        // Actualizar estado
        setUsuario(usuarioData);
        
        return { success: true };
      }
      
      return { success: false, message: response.message };
    } catch (error) {
      console.error('Error en login:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al iniciar sesión' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('usuario');
    setUsuario(null);
  };

  const handleSessionExpiredConfirm = () => {
    setSessionExpired(false);
    logout();
  };

  const register = async (datosUsuario) => {
    try {
      const response = await authService.register(datosUsuario);
      return response;
    } catch (error) {
      console.error('Error en registro:', error);
      throw error;
    }
  };

  // Función para verificar si el usuario tiene un permiso específico
  const tienePermiso = (codigoPermiso) => {
    if (!usuario || !usuario.rol || !usuario.rol.permisos) {
      return false;
    }
    return usuario.rol.permisos.some(p => p.codigo === codigoPermiso);
  };

  // Función para obtener el nivel del rol
  const getNivelRol = () => {
    if (!usuario || !usuario.rol) {
      return 0;
    }
    return usuario.rol.nivel || 0;
  };

  const value = {
    usuario,
    loading,
    login,
    logout,
    register,
    tienePermiso,
    getNivelRol,
    isAuthenticated: !!usuario
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <ConfirmModal
        isOpen={sessionExpired}
        title="Sesión expirada"
        message="Tu sesión ha expirado. Por favor, inicia sesión nuevamente para continuar."
        onConfirm={handleSessionExpiredConfirm}
        onCancel={() => {}}
        confirmLabel="Iniciar sesión"
        showCancelButton={false}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};